/**
 * Resolução de URLs da API e do WebSocket.
 *
 * No navegador a base relativa (`/api`, `ws://<host>/ws`) funciona via proxy
 * do Vite/nginx — zero configuração. No app NATIVO (Capacitor/Android) o
 * WebView é servido de `http://localhost`, então `/api` apontaria para o
 * próprio aparelho. Por isso o app nativo usa:
 *
 *   1. override salvo no aparelho (ajustável em Configurações → "Servidor"),
 *   2. senão o `VITE_API_URL`/`VITE_WS_URL` embutidos no build,
 *   3. senão relativo (último recurso).
 */
import { Capacitor } from '@capacitor/core'

export const API_BASE_STORAGE_KEY = 'lupybot:apiBase'
export const WS_BASE_STORAGE_KEY = 'lupybot:wsBase'

function isNative(): boolean {
  return Capacitor.isNativePlatform()
}

function fromStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Base HTTP da API (absoluta ou `/api` relativo). */
export function getApiBaseUrl(): string {
  if (isNative()) {
    const saved = fromStorage(API_BASE_STORAGE_KEY)
    if (saved && saved.trim()) return saved.trim()
  }
  const raw = import.meta.env.VITE_API_URL
  return raw && raw.trim() ? raw.trim() : '/api'
}

/**
 * URL base do WebSocket. No app nativo, deriva da base da API
 * (http→ws + sufixo /ws) quando não há override explícito de WS.
 */
export function getWsBaseUrl(): string {
  if (isNative()) {
    const saved = fromStorage(WS_BASE_STORAGE_KEY)
    if (saved && saved.trim()) return saved.trim()
    const apiBase = getApiBaseUrl()
    if (apiBase.startsWith('http')) {
      return `${apiBase.replace(/^http/, 'ws').replace(/\/$/, '')}/ws`
    }
  }
  const raw = import.meta.env.VITE_WS_URL
  return raw && raw.trim() ? raw.trim() : ''
}

/** Persiste a URL do servidor escolhida no app nativo. */
export function setServerBaseUrls(apiBase: string, wsBase: string): void {
  try {
    window.localStorage.setItem(API_BASE_STORAGE_KEY, apiBase.trim())
    window.localStorage.setItem(WS_BASE_STORAGE_KEY, wsBase.trim())
  } catch {
    /* storage indisponível — segue sem persistir */
  }
}

/** Volta para a URL padrão do build (limpa o override do aparelho). */
export function resetServerBaseUrls(): void {
  try {
    window.localStorage.removeItem(API_BASE_STORAGE_KEY)
    window.localStorage.removeItem(WS_BASE_STORAGE_KEY)
  } catch {
    /* noop */
  }
}
